import React from 'react';
import { useNotification } from '@dispatch/web/ui';
import { BulkTaskActionDrawer } from '@dispatch/web/features/tasks';
import {
  BulkTaskLockResponse,
  TaskInfo,
  TasksListFilter,
} from '@dispatch/shared/types';
import { useBulkLockTasks } from '@dispatch/shared/hooks';

const taskWord = (n: number) => (n === 1 ? 'task' : 'tasks');

// Pushes the same exclusions the legacy `filterLockableRow` predicate did
// (locked / terminal queue / unassigned) into the server query so each
// paginated page returns a full, stable count and the drawer can render the
// response as-is. The API supports all three filters:
//   - `locked: false`         → DispatchTaskService excludes rows with LockedAt
//   - `status: 'assigned'`    → requires non-empty AssignedResource.Id
//   - `queueName: [...]`      → inclusion list; omitting `egress` and
//                               `cancelled_q` matches the legacy queue check
// `wip_q` and `input_q` are INCLUDED to match the legacy front-end behavior
// — `wip_q` tasks reach the lock endpoint and come back as
// `skipped: in_progress`; `input_q` tasks are virtually always unassigned
// and so are filtered out by `status: 'assigned'` anyway.
//
// `hideCancelledTasks: false` does NOT bring cancelled tasks back — they
// stay hidden because `cancelled_q` is not in our `queueName` list above.
// The flag is misleadingly named: it actually triggers a mutation inside
// `useGetTaskInfos` (UseGetTasks.ts:25-34) that overwrites the caller's
// `queueName` with a list that includes the terminal `egress` queue and
// drops `input_q`. That mutation would defeat our carefully built list.
// The dispatcher's "unassigned" quick filter (TasksList.tsx) flips
// `hideCancelledTasks` to true and the value flows through. Forcing it
// to `false` here keeps our `queueName` (which excludes both
// `cancelled_q` AND `egress`) intact.
//
// KNOWN LANDMINE: `useGetTaskInfos` also forces `locked: true` and a
// self-scoped `assignedResourceIds` when the caller is an adjuster
// (UseGetTasks.ts:20-23). The override below cannot stop that branch —
// only the permissions gate on the Bulk Lock menu (TasksList.tsx, which
// requires `update:task`) keeps adjusters out today. If that gate ever
// loosens, the drawer will start returning each adjuster's locked
// tasks — the opposite of the bulk-lock intent. The real fix is to drop
// the adjuster mutation from the shared hook.
const LOCKABLE_QUERY_OVERRIDES: Partial<TasksListFilter> = {
  locked: false,
  status: 'assigned',
  hideCancelledTasks: false,
  queueName: [
    'input_q',
    'auto_dispatch_q',
    'manual_dispatch_q',
    'dispatched_q',
    'wip_q',
    'needs_attention_q',
  ],
};

const formatOutcomeBreakdown = (data: BulkTaskLockResponse): string => {
  const parts: string[] = [];
  if (data.skippedCount > 0) parts.push(`${data.skippedCount} skipped`);
  if (data.failedCount > 0) parts.push(`${data.failedCount} failed`);
  return parts.join(', ');
};

// Wrapped in <span> blocks (rendered display: block) instead of <p> because
// ConfirmModal renders confirmContent inside <Typography component="p">; an
// inner <p> triggers an invalid-DOM-nesting warning and is stripped by
// some browsers.
const renderLockContent = (selectedCount: number): React.ReactElement => (
  <>
    <span style={{ display: 'block', marginBottom: 8 }}>
      Dispatch will lock these tasks and alert the resource.
    </span>
    <span style={{ display: 'block' }}>
      <b>{`${selectedCount} ${taskWord(selectedCount)}`}</b> will be locked.
    </span>
  </>
);

export const BulkTaskLock = () => {
  const { notifySuccess, notifyWarning, notifyError } = useNotification();

  // No navigate() on success: the drawer stays mounted so the dispatcher
  // can keep working through their backlog. `useBulkLockTasks` invalidates
  // the `['infos']` query in `onSettled` (see `invalidateTaskCaches`), which
  // triggers a refetch of the drawer's `useGetTaskInfos` and removes the
  // just-locked rows from the grid.
  const { mutate: bulkLock, status: bulkLockStatus } = useBulkLockTasks(
    (data: BulkTaskLockResponse) => {
      if (data.lockedCount > 0) {
        notifySuccess(
          '',
          `${data.lockedCount} ${taskWord(data.lockedCount)} locked`
        );
      }

      // If any task was skipped or failed, surface a secondary toast with the
      // breakdown so the dispatcher knows the full operation outcome.
      if (data.skippedCount + data.failedCount > 0) {
        notifyWarning('Bulk lock summary', formatOutcomeBreakdown(data));
      }
    },
    () => notifyError('Error', 'Tasks not locked')
  );

  const handleConfirmLock = (selectedRows: TaskInfo[]) => {
    bulkLock({ taskIds: selectedRows.map((row) => row.id) });
  };

  return (
    <BulkTaskActionDrawer
      title="Lock Tasks"
      actionButton={{
        label: 'Lock Tasks',
        color: 'warning',
        testId: 'lock-task-button',
      }}
      modalType="bulkLockTask"
      noRowsMessage="No Tasks available to lock in bulk."
      queryFilterOverrides={LOCKABLE_QUERY_OVERRIDES}
      renderConfirm={renderLockContent}
      confirmTitle={(count) => `Lock ${count} ${taskWord(count)}?`}
      actionStatus={bulkLockStatus}
      onConfirm={handleConfirmLock}
    />
  );
};
